import { VisitDatetimeField, VisitOutcome } from '@/types/visit';
import { Ordering } from 'gql-types';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    ContainerDefinitionType,
    defaultPatientsRowToLink,
    defaultVisitsRowToLink,
    makeContainerDefinition,
} from '../definitions';
import { PatientDashboardContainer, RootEntity, VisitDashboardContainer } from '../types';
import { COFK_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

// Care Options for Kids (COFK) is a pediatric-therapy organization (OT / PT /
// SLP). Its work queues copy the logic from the Pinnacle organization for
// Evals, Patients Needing Scheduling, and Visit Scheduled After Discipline
// Discharge, plus a Visit Sync Errors queue modeled on Eleanor's sync queues.
//
// COFK does not have episodes of care enabled, so all queue filters and row
// links are episode-less.

// COFK Eval visit service external IDs (SERVICE_PRIMARY_KEY), provided by the
// business. Mirrors Pinnacle's EVAL_SERVICE_IDS anchor list.
// TODO(COFK): add "COFK SLPI Feeding Eval" once its external ID is identified
// (no match at the time of writing).
const EVAL_SERVICE_IDS = [
    'AQgs1igzRC', // COFK OT Feeding Eval
    'VHZXmZF31w', // COFK PT Eval
    'vIumzDoNU0', // COFK SLP Eval
    '2uLY5XM6fr', // COFK SLP Feeding Eval
    'tCSbyJwvFC', // COFK OT Eval
    'ySGjr3EWPH', // COFK HH OT Eval
    'Uk77LWZg9D', // COFK HH PT Eval
    'uEVM2YK1rn', // COFK HH SLP Eval
    '6XfcgxZben', // COFK OASIS OT Eval
    'AeEDs1igzR', // COFK OASIS PT Eval
    'lN7niGAYKI', // COFK OASIS SLP Eval
];

// Eval visits that have not yet been processed. Adapted from Pinnacle's
// EVAL_CONTAINER, but episode-less (COFK has no episodes).
// 1. A visit has a service code in EVAL_SERVICE_IDS
// 2. That visit is missing one of the following: clinician or date
// sorting: alphabetical by patient full name
const EVAL_CONTAINER: VisitDashboardContainer = {
    key: COFK_CONTAINER_KEYS.eval,
    label: 'Eval',
    defaultEnabled: true,
    hoverText: 'Evals that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
            needsAssignment: { exact: true },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Eval visits to schedule.',
        ordering: { patient: { fullName: Ordering.ASC } },
    },
};

// Patients with visits that still need a service, clinician, or date. Adapted
// from Pinnacle's PATIENTS_NEEDING_SCHEDULING_CONTAINER, but episode-less.
// 1. A patient has at least one visit missing service, clinician, or date
// 2. The patient is active
// sorting: alphabetical by patient full name
const PATIENTS_NEEDING_SCHEDULING_CONTAINER: PatientDashboardContainer = {
    key: COFK_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Patients with visits that need to be scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasVisits: {
                needsAssignment: { exact: true },
            },
        },
        rowToLink: defaultPatientsRowToLink,
        emptyTableText: 'No patients to schedule.',
        ordering: { fullName: Ordering.ASC },
    },
};

// ---------------------------------------------------------------------------
// Visit Scheduled After Discipline Discharge (STUB)
// ---------------------------------------------------------------------------
// Copied from Pinnacle's DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS
// container. The *logic* is complete, but the three COFK-specific service-ID
// lists below still need real values from the business.
//
// The discharge anchor is seeded with a sentinel that can never match a real
// service, so this queue returns zero rows until the TODOs are resolved (it is
// therefore safe to ship default-enabled).

// TODO(COFK): replace this sentinel with COFK's discipline-discharge visit
// service external IDs (per discipline: OT / PT / SLP). Until then the sentinel
// guarantees the queue stays empty regardless of how the backend treats an
// empty inList.
const COFK_DISCIPLINE_DISCHARGE_TODO_SENTINEL = '__COFK_DISCIPLINE_DISCHARGE_TODO__';
const ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS = [COFK_DISCIPLINE_DISCHARGE_TODO_SENTINEL];

// A completed Eval (or ROC, if COFK has one) in the same discipline "re-admits"
// the patient and nullifies a prior discharge (mirrors Pinnacle FE-649). COFK
// has no ROC concept today, so this is seeded with the Eval IDs.
// TODO(COFK): extend if COFK introduces ROC-equivalent re-admission services.
const DISCIPLINE_READMISSION_SERVICE_IDS = [...EVAL_SERVICE_IDS];

// Service codes that should NOT count as "pending visits needing scheduler
// action" after a discharge (non-visit tasks, summaries, etc.).
// TODO(COFK): populate with COFK's non-visit / administrative service IDs.
const POST_DISCHARGE_EXCLUDED_SERVICE_IDS: string[] = [];

// Discipline Discharge Patients with Pending Visits
// Row = a pending visit that needs canceling, NOT the discharge itself.
// Inclusion:
// 1. Visit has outcome = NONE.
// 2. The visit's service code is NOT in POST_DISCHARGE_EXCLUDED_SERVICE_IDS.
// 3. The patient has a related completed discipline-discharge visit in the
//    same discipline whose effectiveDate is BEFORE this pending visit's, AND
//    that discharge has NOT been nullified by a later completed same-discipline
//    Eval (re-admission).
// Sorting: by earliest date of upcoming visits.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = {
    key: COFK_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    defaultEnabled: true,
    hoverText: 'Discipline discharged patients with scheduled visits that need to be canceled.',
    heavyQuery: true,
    extraProps: { bulkActions: [ContainerBulkAction.CANCEL_VISITS] },
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        highlightLinkedVisit: true,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            NOT: {
                services: {
                    externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
                },
            },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                sameDiscipline: true,
                filter: {
                    services: {
                        externalIds: { value: { inList: ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS } },
                    },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    // Discharge is nullified if a later same-discipline Eval
                    // re-admitted the patient.
                    NOT: {
                        relatedVisitCondition: {
                            exists: true,
                            samePatient: true,
                            sameDiscipline: true,
                            filter: {
                                services: {
                                    externalIds: {
                                        value: {
                                            inList: DISCIPLINE_READMISSION_SERVICE_IDS,
                                        },
                                    },
                                },
                                outcome: { exact: VisitOutcome.COMPLETED },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gtOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        ltOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No pending visits after discipline discharge.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

export const CARE_OPTIONS_FOR_KIDS_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        /* #1 */ EVAL_CONTAINER,
        /* #2 */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #3 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        // #4 Visit Sync Errors — modeled on Eleanor's sync queues, but scoped to
        // the visit entity (VISIT_SYNC_ISSUES) instead of patients/appointments.
        makeContainerDefinition({
            type: ContainerDefinitionType.VISIT_SYNC_ISSUES,
            key: COFK_CONTAINER_KEYS.visitSyncIssues,
            defaultEnabled: true,
        }),
    ],
};
